import React, { useState, useEffect } from 'react'
import { api } from '../services/authService'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  PhotoIcon,
  PresentationChartBarIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const CONTENT_TYPES = [
  { value: 'case_study', label: 'Case de Sucesso', icon: '📋' },
  { value: 'article', label: 'Artigo Técnico', icon: '📝' },
  { value: 'comparison', label: 'Comparativo', icon: '📊' },
  { value: 'video', label: 'Vídeo', icon: '🎥' },
  { value: 'guide', label: 'Guia Prático', icon: '📖' },
  { value: 'infographic', label: 'Infográfico', icon: '📈' },
  { value: 'presentation', label: 'Apresentação', icon: '📽️' },
  { value: 'proposal_template', label: 'Template de Proposta', icon: '📄' }
]

const SECTORS = [
  'Construção Civil', 'Mineração', 'Pintura Viária', 'Saneamento',
  'Energia', 'Agronegócio', 'Logística', 'Indústria', 'Geral'
]

const MarketingContentPage = () => {
  const [contents, setContents] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterSector, setFilterSector] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingContent, setEditingContent] = useState(null)

  useEffect(() => {
    loadContents()
  }, [filterType, filterSector])

  const loadContents = async () => {
    setLoading(true)
    try {
      const params = {}
      if (filterType) params.content_type = filterType
      if (filterSector) params.target_sector = filterSector
      if (search) params.search = search

      const res = await api.get('/api/marketing/content', { params })
      setContents(res.data || [])
    } catch (error) {
      console.error('Erro ao carregar conteúdos:', error)
      toast.error('Erro ao carregar biblioteca de conteúdo')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este conteúdo?')) return
    try {
      await api.delete(`/api/marketing/content/${id}`)
      toast.success('Conteúdo excluído')
      loadContents()
    } catch (error) {
      toast.error('Erro ao excluir')
    }
  }

  const handleSave = async (formData) => {
    try {
      if (editingContent) {
        await api.put(`/api/marketing/content/${editingContent.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        toast.success('Conteúdo atualizado')
      } else {
        await api.post('/api/marketing/content', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        toast.success('Conteúdo criado')
      }
      setShowForm(false)
      setEditingContent(null)
      loadContents()
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar')
    }
  }

  const getTypeInfo = (type) => CONTENT_TYPES.find(t => t.value === type) || { label: type, icon: '📄' }

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">Biblioteca de Conteúdo</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Cases, artigos, materiais técnicos e propostas</p>
        </div>
        <button
          onClick={() => { setEditingContent(null); setShowForm(true) }}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
        >
          <PlusIcon className="w-4 h-4" />
          Novo Conteúdo
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por título..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadContents()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
          >
            <option value="">Todos os tipos</option>
            {CONTENT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
            ))}
          </select>
          <select
            value={filterSector}
            onChange={e => setFilterSector(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
          >
            <option value="">Todos os setores</option>
            {SECTORS.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Grid de Conteúdos */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      ) : contents.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center py-16 text-gray-500">
          <FolderOpenIcon className="w-16 h-16 mb-4 text-gray-300" />
          <p className="text-lg font-medium">Nenhum conteúdo encontrado</p>
          <p className="text-sm mt-1">Crie seu primeiro material de marketing</p>
          <button
            onClick={() => { setEditingContent(null); setShowForm(true) }}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            Criar Conteúdo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {contents.map(content => {
            const typeInfo = getTypeInfo(content.content_type)
            return (
              <div key={content.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition">
                {/* Header com tipo */}
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                    <span>{typeInfo.icon}</span>
                    {typeInfo.label}
                  </span>
                  {content.target_sector && (
                    <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full">
                      {content.target_sector}
                    </span>
                  )}
                </div>

                {/* Body */}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 dark:text-white mb-2 line-clamp-2">{content.title}</h3>
                  {content.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-3">{content.description}</p>
                  )}

                  {/* Métricas */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <EyeIcon className="w-3.5 h-3.5" />
                      {content.views || 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                      {content.downloads || 0} downloads
                    </span>
                  </div>

                  {/* Tags */}
                  {content.tags && content.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {content.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {new Date(content.created_at).toLocaleDateString('pt-BR')}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setEditingContent(content); setShowForm(true) }}
                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      title="Editar"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(content.id)}
                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Excluir"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Modal de Formulário */}
      {showForm && (
        <ContentFormModal
          content={editingContent}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingContent(null) }}
        />
      )}
    </div>
  )
}

// Modal de Formulário de Conteúdo
const ContentFormModal = ({ content, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: content?.title || '',
    content_type: content?.content_type || '',
    target_sector: content?.target_sector || '',
    description: content?.description || '',
    content: content?.content || '',
    tags: content?.tags?.join(', ') || ''
  })
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.title.trim() || !form.content_type) {
      toast.error('Título e tipo são obrigatórios')
      return
    }

    const formData = new FormData()
    formData.append('title', form.title)
    formData.append('content_type', form.content_type)
    if (form.target_sector) formData.append('target_sector', form.target_sector)
    if (form.description) formData.append('description', form.description)
    if (form.content) formData.append('content', form.content)
    if (form.tags) {
      const tagsArray = form.tags.split(',').map(t => t.trim()).filter(Boolean)
      formData.append('tags', JSON.stringify(tagsArray))
    }
    if (file) formData.append('file', file)

    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white">
            {content ? 'Editar Conteúdo' : 'Novo Conteúdo'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título *</label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo *</label>
              <select
                value={form.content_type}
                onChange={e => setForm(prev => ({ ...prev, content_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
                required
              >
                <option value="">Selecione...</option>
                {CONTENT_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Setor-alvo</label>
              <select
                value={form.target_sector}
                onChange={e => setForm(prev => ({ ...prev, target_sector: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              >
                <option value="">Todos</option>
                {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Descrição</label>
            <textarea
              value={form.description}
              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conteúdo</label>
            <textarea
              value={form.content}
              onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
              placeholder="Texto do conteúdo, descrição detalhada, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (separadas por vírgula)</label>
            <input
              type="text"
              value={form.tags}
              onChange={e => setForm(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="munck, construção, case"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Arquivo (PDF, imagem, vídeo)</label>
            <input
              type="file"
              onChange={e => setFile(e.target.files[0])}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-green-50 file:text-green-700"
            />
            {content?.file_url && <p className="text-xs text-gray-500 mt-1">Arquivo atual: {content.file_url}</p>}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {saving ? 'Salvando...' : (content ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MarketingContentPage
